import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { Radio, Plus, ArrowRight, ArrowLeftRight, Send, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import { app } from "../firebase";
import { useStreams } from "../hooks/useStreams";
import {
  fetchQuota,
  requestFormProblem,
  submitStreamRequest,
  StreamQuota,
  MIN_REASON_LENGTH,
  MAX_REASON_LENGTH,
} from "../utils/streamRequests";
import { trackClick } from "../utils/analytics";
import { fetchTransfers } from "../utils/streamTransfer";

const StreamsPage: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(getAuth(app));
  const { streams, selected, select, loading: streamsLoading } = useStreams();
  const [offersWaiting, setOffersWaiting] = useState(0);

  const [quota, setQuota] = useState<StreamQuota | null>(null);
  const [quotaFailed, setQuotaFailed] = useState(false);
  const [count, setCount] = useState("");
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState("");

  const loadQuota = useCallback(async () => {
    if (!user) return;
    try {
      const loaded = await fetchQuota(() => user.getIdToken());
      setQuota(loaded);
      setQuotaFailed(false);
      setCount((current) => current || String(Math.min(loaded.limit + 1, loaded.max)));
    } catch (error) {
      console.error(error);
      setQuotaFailed(true);
    }
  }, [user]);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  useEffect(() => {
    if (!user) return;
    fetchTransfers(() => user.getIdToken()).then((t) => setOffersWaiting(t.incoming.length)).catch(() => undefined);
  }, [user]);

  const open = (name: string) => {
    select(name);
    navigate("/");
  };

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !quota) return;
    const wanted = Number(count);
    const invalid = requestFormProblem(wanted, reason, quota);
    if (invalid) { setProblem(invalid); return; }
    setSending(true);
    setProblem("");
    trackClick("button_click-request_streams");
    const result = await submitStreamRequest(() => user.getIdToken(), wanted, reason.trim());
    setSending(false);
    if (result.ok) { setReason(""); await loadQuota(); }
    else setProblem(result.message);
  };

  const canAdd = !!quota && quota.used < quota.limit;

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="streams" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Streams</h1>
                <p className="text-slate-500 mt-1 text-lg">
                  {quota ? `You're using ${quota.used} of ${quota.limit} stream${quota.limit === 1 ? "" : "s"}.` : "Manage the streams on your account."}
                </p>
              </div>
              <button
                onClick={() => navigate("/streams/new")}
                disabled={!canAdd}
                title={quota && !canAdd ? "You've reached your limit. Request more below." : undefined}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                <Plus size={18} /> Add a stream
              </button>
            </div>

            {quotaFailed && (
              <div role="alert" className="p-4 rounded-2xl bg-red-50 text-red-700 border border-red-100 flex items-center gap-3 text-sm font-medium">
                <AlertCircle size={18} /> We couldn't load your stream limit. Refresh to try again.
              </div>
            )}

            {offersWaiting > 0 && (
              <Link to="/transfers" className="p-4 rounded-2xl bg-blue-50 text-blue-800 border border-blue-100 flex items-center gap-3 text-sm font-medium hover:bg-blue-100">
                <ArrowLeftRight size={18} /> {offersWaiting === 1 ? "A stream was offered to you." : `${offersWaiting} streams were offered to you.`} Review
              </Link>
            )}

            {!streamsLoading && streams.length === 0 && (
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-900 mb-2">This account has no streams</h2>
                <p className="text-slate-500 text-sm mb-6">
                  Add a new one, accept a stream offered to you, or delete the account if you no longer need it.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => navigate("/streams/new")} className="btn-primary">Add a stream</button>
                  <Link to="/profile" className="btn-secondary">Delete my account</Link>
                </div>
              </div>
            )}

            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-50 rounded-lg text-blue-600"><Radio size={20} /></div>
                <h2 className="text-lg font-bold text-slate-900">Your streams</h2>
              </div>
              <ul className="divide-y divide-slate-100">
                {streams.map((s) => (
                  <li key={s.name} className="py-3 flex items-center gap-4">
                    {s.imageUrl ? (
                      <img src={s.imageUrl} alt="" className="w-10 h-10 rounded-xl object-contain bg-slate-50" />
                    ) : (
                      <span className="w-10 h-10 rounded-xl" style={{ backgroundColor: s.color || "#888888" }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 break-all">{s.name}</p>
                      {selected?.name === s.name && <p className="text-xs text-blue-600 font-medium">Showing now</p>}
                    </div>
                    <Link to={`/transfers?stream=${encodeURIComponent(s.name)}`} className="btn-secondary flex items-center gap-2">
                      <ArrowLeftRight size={16} /> Transfer
                    </Link>
                    <button onClick={() => open(s.name)} className="btn-secondary flex items-center gap-2">
                      Open <ArrowRight size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {quota && (
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600"><Send size={20} /></div>
                  <h2 className="text-lg font-bold text-slate-900">Need a different number of streams?</h2>
                </div>
                <p className="text-slate-500 text-sm mb-6">Tell us how many you need and why. We review each request, and extra streams are free once approved.</p>

                {quota.last && (
                  <div className={`mb-6 p-4 rounded-2xl border flex items-start gap-3 ${quota.last.status === "approved" ? "bg-green-50 text-green-700 border-green-100" : "bg-slate-50 text-slate-700 border-slate-200"}`}>
                    {quota.last.status === "approved" ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <AlertCircle size={18} className="mt-0.5 shrink-0" />}
                    <div className="text-sm">
                      <p className="font-medium">
                        {quota.last.status === "approved"
                          ? `Your request for ${quota.last.count} streams was approved.`
                          : `Your request for ${quota.last.count} streams wasn't approved.`}
                      </p>
                      {quota.last.note && <p className="mt-1">{quota.last.note}</p>}
                    </div>
                  </div>
                )}

                {quota.pending ? (
                  <div className="p-4 rounded-2xl bg-amber-50 text-amber-800 border border-amber-100 flex items-start gap-3">
                    <Clock size={18} className="mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium">Your request for {quota.pending.count} streams is waiting for review.</p>
                      <p className="mt-1 whitespace-pre-wrap break-words">{quota.pending.reason}</p>
                      <p className="mt-2">We'll email you when it's decided.</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={send} className="space-y-5">
                    <div>
                      <label htmlFor="stream-count" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">
                        How many streams do you want in total? (1 to {quota.max})
                      </label>
                      <input
                        id="stream-count"
                        type="number"
                        min={1}
                        max={quota.max}
                        value={count}
                        onChange={(e) => setCount(e.target.value)}
                        className="w-32 bg-white border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="stream-reason" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">Why do you need them?</label>
                      <textarea
                        id="stream-reason"
                        rows={4}
                        maxLength={MAX_REASON_LENGTH}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="For example: we run three rooms at the same conference."
                        className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      />
                      <p className="text-xs text-slate-400 mt-1 ml-1">At least {MIN_REASON_LENGTH} characters.</p>
                    </div>
                    {problem && (
                      <div role="alert" className="p-3 rounded-2xl bg-red-50 text-red-700 border border-red-100 text-sm font-medium flex items-center gap-2">
                        <AlertCircle size={18} /> {problem}
                      </div>
                    )}
                    <button type="submit" disabled={sending} className="btn-primary flex items-center gap-2 disabled:opacity-60">
                      <Send size={18} /> {sending ? "Sending..." : "Send request"}
                    </button>
                  </form>
                )}

                {quota.isAdmin && (
                  <p className="mt-6 text-sm">
                    <Link to="/stream-requests" className="text-blue-600 hover:underline font-medium">Review everyone's requests</Link>
                  </p>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default StreamsPage;
